import React, { useEffect, useState } from 'react';
import { Eye, Globe2, LoaderCircle, LockKeyhole } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceLayout } from '../../components/layout/WorkspaceLayout';
import { Button } from '../../components/ui/Button';
import { ProjectEditForm } from '../../features/projects/ProjectEditForm';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';
import { fetchProjectById, updateProject } from '../../redux/slices/projectsSlice';

const SkeletonBlock = ({ className = '' }) => (
  <div className={`animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-800 ${className}`} />
);

const EditPageSkeleton = () => (
  <div className="grid gap-6 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
    <div className="space-y-4">
      <SkeletonBlock className="h-[170px] w-full" />
      <SkeletonBlock className="h-40 w-full" />
    </div>
    <div className="space-y-4">
      <SkeletonBlock className="h-56 w-full" />
      <SkeletonBlock className="h-36 w-full" />
    </div>
  </div>
);

export const ProjectEditPage = () => {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t, formatNumber } = useI18n();
  const { items, error, detailLoading } = useAppSelector((state) => state.projects);
  const [saving, setSaving] = useState(false);
  const [loadState, setLoadState] = useState('loading');

  const project = items.find((item) => item.id === projectId) || null;
  const returnTo = location.state?.returnTo || '/projects';
  const isPublic = project?.visibility === 'public';

  useEffect(() => {
    if (!projectId) {
      setLoadState('not-found');
      return undefined;
    }

    let cancelled = false;

    const loadProject = async () => {
      setLoadState('loading');

      try {
        await dispatch(fetchProjectById(projectId)).unwrap();

        if (!cancelled) {
          setLoadState('ready');
        }
      } catch {
        if (!cancelled) {
          setLoadState('not-found');
        }
      }
    };

    loadProject();

    return () => {
      cancelled = true;
    };
  }, [dispatch, projectId]);

  const handleCancel = () => {
    navigate(returnTo);
  };

  const handleSubmit = async (payload) => {
    setSaving(true);

    const action = await dispatch(updateProject(payload));

    setSaving(false);

    if (updateProject.fulfilled.match(action)) {
      navigate(returnTo);
    }
  };

  const pageContent = (() => {
    if (loadState === 'loading' || (detailLoading && !project)) {
      return <EditPageSkeleton />;
    }

    if (loadState === 'not-found' || !project) {
      return (
        <section className="rounded-xl border border-neutral-200 bg-white p-8 text-center dark:border-neutral-800 dark:bg-neutral-950">
          <p className="text-sm text-neutral-500">{t('projectNotFound')}</p>
          <Button className="mt-4" variant="secondary" onClick={handleCancel}>
            {t('projectEditBack')}
          </Button>
        </section>
      );
    }

    return (
      <>
        <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {isPublic && typeof project.viewCount === 'number' ? (
              <span className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs font-medium text-neutral-500 dark:border-neutral-800 dark:bg-neutral-950">
                <Eye size={13} />
                {t('publicTemplatesViewCount', { count: formatNumber(project.viewCount) })}
              </span>
            ) : null}
            <span
              className={classNames(
                'inline-flex w-fit items-center gap-1.5 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs font-medium text-neutral-600 dark:border-neutral-800 dark:text-neutral-300'
              )}
            >
              {isPublic ? <Globe2 size={13} /> : <LockKeyhole size={13} />}
              {isPublic ? t('public') : t('private')}
            </span>
          </div>
          <Button type="submit" form="project-edit-form" disabled={saving}>
            {saving ? (
              <>
                <LoaderCircle size={16} className="animate-spin" />
                {t('updatingProject')}
              </>
            ) : (
              t('save')
            )}
          </Button>
        </header>

        <ProjectEditForm project={project} error={error} saving={saving} onSubmit={handleSubmit} />
      </>
    );
  })();

  return (
    <WorkspaceLayout
      project={project || { id: projectId, name: t('projectSettings') }}
      backTo={returnTo}
      backAriaLabel={returnTo.startsWith('/workspace') ? t('backToWorkspace') : t('backToProjects')}
      activeSection="settings"
    >
      {pageContent}
    </WorkspaceLayout>
  );
};
